import * as React from 'react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../Authentication/components/AuthContext';
import AppShell from './AppShell';
import { PageHeadActions, SubNav } from '../components/nav';
import { TCard, TEmpty } from '../components/table';

/*
 * Frame for the Organization and Admin areas: page head, a tab row, and a
 * role check (org pages: org admins + global admins; admin: global admins).
 * The server enforces the same rules; this keeps people off pages they
 * can't use.
 */

export type Area = 'org' | 'admin';

const TABS: Record<Area, Array<{ id: string; label: string; href: string }>> = {
    org: [
        { id: 'profile', label: 'Profile', href: '/org' },
        { id: 'stats', label: 'Stats', href: '/org/stats' },
        { id: 'accounts', label: 'Accounts', href: '/org/accounts' },
        { id: 'whitelabel', label: 'White-label', href: '/org/whitelabel' },
        { id: 'templates', label: 'Email templates', href: '/org/templates' },
        { id: 'integrations', label: 'Integrations', href: '/org/integrations' },
    ],
    admin: [
        { id: 'orgs', label: 'Organizations', href: '/admin' },
        { id: 'licenses', label: 'Licenses', href: '/admin/licenses' },
        { id: 'features', label: 'Features', href: '/admin/features' },
        { id: 'templates', label: 'Templates', href: '/admin/templates' },
        { id: 'gallery-users', label: 'Gallery users', href: '/admin/gallery-users' },
        { id: 'announcements', label: 'Announcements', href: '/admin/announcements' },
        { id: 'global-settings', label: 'Global settings', href: '/admin/global-settings' },
        { id: 'system-health', label: 'System health', href: '/admin/system-health' },
        { id: 'stats', label: 'Stats', href: '/admin/stats' },
        { id: 'sync', label: 'v3 sync', href: '/admin/sync' },
    ],
};

const GLOBAL_ADMIN = 69;
const ORG_ADMIN = 0;

export function useRoles() {
    const { auth } = useContext(AuthContext);
    const roles: number[] = Array.isArray(auth?.roles) ? auth.roles : [];
    return {
        globalAdmin: roles.includes(GLOBAL_ADMIN),
        orgAdmin: roles.includes(ORG_ADMIN) || roles.includes(GLOBAL_ADMIN),
    };
}

export default function AreaShell({
    area,
    tab,
    title,
    sub,
    actions,
    children,
}: {
    area: Area;
    /** id of the active tab */
    tab: string;
    title?: string;
    sub?: React.ReactNode;
    actions?: React.ReactNode;
    children: React.ReactNode;
}) {
    const navigate = useNavigate();
    const { globalAdmin, orgAdmin } = useRoles();
    const allowed = area === 'admin' ? globalAdmin : orgAdmin;
    const areaTitle = area === 'admin' ? 'Admin' : 'Organization';
    return (
        <AppShell>
            <PageHeadActions title={title ?? areaTitle} sub={sub} actions={allowed ? actions : undefined} />
            {allowed ? (
                <>
                    <SubNav
                        items={TABS[area].map((t) => ({ id: t.id, label: t.label, href: t.href }))}
                        active={tab}
                        onSelect={(id) => {
                            const t = TABS[area].find((x) => x.id === id);
                            if (t) navigate(t.href);
                        }}
                        label={`${areaTitle} sections`}
                    />
                    {children}
                </>
            ) : (
                <TCard>
                    <TEmpty
                        message={
                            area === 'admin'
                                ? 'Admin is for Smilebooth global admins.'
                                : 'Organization settings are for org admins.'
                        }
                        actionLabel="Go home"
                        onAction={() => navigate('/home')}
                    />
                </TCard>
            )}
        </AppShell>
    );
}
